import { z } from 'zod';
import {
  type FeatureFlagInput,
  type FeatureFlagOutput,
  type FeatureFlagDefinition,
} from '../utils/resolve-feature-flags/resolve-feature-flags';

const booleanSchema = z
  .union([z.literal('0'), z.literal('1')])
  .transform((value) => {
    return value === '1';
  });

export const flagDefinitions = {
  'web.feature-flag.salad-shooter.enabled': {
    name: 'web.feature-flag.salad-shooter.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.delivery-rating-module.enabled': {
    name: 'web.feature-flag.delivery-rating-module.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.ai-opt-out.enabled': {
    name: 'web.feature-flag.ai-opt-out.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.recipe-from-ingredients.enabled': {
    name: 'web.feature-flag.recipe-from-ingredients.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.update-stripe-payment-element.enabled': {
    name: 'web.feature-flag.update-stripe-payment-element.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.coming-soon.enabled': {
    name: 'web.feature-flag.coming-soon.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.floaty.enabled': {
    name: 'web.feature-flag.floaty.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.carty-search.enabled': {
    name: 'web.feature-flag.carty-search.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.two-three-four-five-servings.enabled': {
    name: 'web.feature-flag.two-three-four-five-servings.enabled',
    schema: booleanSchema,
  },
  // Remote kill-switch for the TEMPORARY exp-242 home-feed video instrumentation:
  // when off, no diagnostic records are sent to Datadog (console is unaffected).
  'web.feature-flag.exp-242-video-instrumentation.enabled': {
    name: 'web.feature-flag.exp-242-video-instrumentation.enabled',
    schema: booleanSchema,
  },

  // Sends `release_date` instead of `inventory_date` to the catalog, inventory
  // and pricing APIs, and reads Algolia's release-date-keyed
  // `index_release_inventory` / `index_release_discounts` (Frictionless
  // Fulfillment, WEB-7500). Read through `useReleaseDateEnabled` in
  // `@hungryroot/rest-hooks`. Resolves to false when the backend has no value,
  // so it ships dark.
  'web.feature-flag.release-date.enabled': {
    name: 'web.feature-flag.release-date.enabled',
    schema: booleanSchema,
  },

  // Sources recipe ingredient swaps from the dedicated
  // `/api/v2/customers/self/pairings/{id}/swaps/` endpoint (candidates
  // hydrated through `/api/v3/pairings/`) instead of the V1
  // `/api/filter_pairings/?swap_ingredients_recipe=` (WEB-7822). Read in
  // `useSwappableRecipesQuery` in `@hungryroot/rest-hooks`. Resolves to false
  // when the backend has no value, so it ships dark.
  'web.feature-flag.swaps-v2-endpoint.enabled': {
    name: 'web.feature-flag.swaps-v2-endpoint.enabled',
    schema: booleanSchema,
  },

  // feature flags shared between BE and FE
  // uses BE naming convention
  'web.login_challenge': {
    name: 'web.login_challenge',
    schema: z.enum(['recaptcha']),
  },
  'web.signup_challenge': {
    name: 'web.signup_challenge',
    schema: z.enum(['recaptcha']),
  },

  /**
   * Turns on customer-created cookbooks — multiple named cookbooks, the default
   * "{Name}'s Saves" cookbook, and heart-to-cookbook routing. Shared with the
   * API and the mobile clients, so it uses the BE naming convention above
   * rather than a `web.feature-flag.` name.
   *
   * Registered here rather than read straight off `useDynamicConfigs` so it
   * honours the `feature-flags.overrides` local-storage escape hatch — that is
   * what the debug menu toggles, and what Playwright sets to pin the pre-2.0
   * cookbook for the suites that assert on it.
   *
   * Deliberately not named for a release: "Cookbooks 2.0" is a project label,
   * while this describes the capability being switched on.
   */
  'api.cookbooks.customer_created_enabled': {
    name: 'api.cookbooks.customer_created_enabled',
    schema: booleanSchema,
  },
  // Serves the SSW Delivery Help flow at `/delivery-help/`; when off, customers
  // get the legacy `auto_issue_credit` flow. Lets SSW go live in production by
  // flipping the config rather than deploying, once its backend ships there.
  // Read through `useSswEnabled` in `@hungryroot/app-mhr`. Resolves to false
  // when the backend has no value or the fetch fails, so it ships dark.
  'web.feature-flag.ssw.enabled': {
    name: 'web.feature-flag.ssw.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.smart-cart-agent-survey.enabled': {
    name: 'web.feature-flag.smart-cart-agent-survey.enabled',
    schema: booleanSchema,
  },
  'web.feature-flag.segment-proxy.enabled': {
    name: 'web.feature-flag.segment-proxy.enabled',
    schema: booleanSchema,
  },

  // Remote pause/relaunch switch for exp 235 (cancellationDeflection): when off,
  // every customer gets the legacy Springroll `/cancel/` flow regardless of their
  // variant. Replaces a hardcoded IS_EXP_235_PAUSED constant so the arm can be
  // paused without a deploy. Resolves to false when the backend has no value, so
  // it ships dark.
  'web.feature-flag.cancellation-deflection.enabled': {
    name: 'web.feature-flag.cancellation-deflection.enabled',
    schema: booleanSchema,
  },

  // Remote switch for the Exp 255 discover nav tiles (Meals, Groceries) on
  // `/discover/`. Read server-side so the grid renders complete on first paint;
  // resolves to false when the backend has no value, so the tiles ship dark
  // and the experiment variant alone does not surface them.
  'web.feature-flag.freemium-nav-links.enabled': {
    name: 'web.feature-flag.freemium-nav-links.enabled',
    schema: booleanSchema,
  },

  // Remote switch for the Exp 255 search bar on `/discover/` (prospects and
  // guests in a treatment variant, mobile only). Read server-side alongside
  // the nav-links flag; resolves to false when the backend has no value, so
  // the search ships dark and the experiment variant alone does not surface it.
  'web.feature-flag.discover-search.enabled': {
    name: 'web.feature-flag.discover-search.enabled',
    schema: booleanSchema,
  },
  // Exp 260: persist a completed, unexpired locally stored quiz to the server
  // when a pre-checkout guest logs in (see `persistLocalQuizOnLogin`).
  'web.feature-flag.login-quiz-persist.enabled': {
    name: 'web.feature-flag.login-quiz-persist.enabled',
    schema: booleanSchema,
  },

  // Remote switch for the Exp 255 guest onboarding tour on `/discover/feed/`
  // (discover → search → cookbook over the guest bottom nav, first visit only).
  // Resolves to false when the backend has no value, so the tour ships dark.
  'web.feature-flag.freemium-onboarding-tour.enabled': {
    name: 'web.feature-flag.freemium-onboarding-tour.enabled',
    schema: booleanSchema,
  },

  // Sends `is_guest_flow: true` on every account-creating request from the
  // mini-quiz signup form (`/mini-quiz/signup/` and the variant-2 cookbook
  // signup): email via `/api/v2/auth/signup/` and Google / Apple / Facebook
  // via the legacy `/api/*_login/` endpoints. The backend creates those
  // customers with `CustomerStatus.GUEST` (BE-8495 / BE-8703). Read in
  // `MiniQuizSignupForm`; resolves to false when the backend has no value, so
  // it ships dark and classic `/signup/` never sends the param.
  'web.feature-flag.mini-quiz-guest-flow.enabled': {
    name: 'web.feature-flag.mini-quiz-guest-flow.enabled',
    schema: booleanSchema,
  },
  // Enables Google Shopping preset-plan capture and the matching plan
  // recommender dynamic-config lookup. Missing values resolve false so web can
  // deploy before the backend support and campaign configs are live.
  'web.feature-flag.google-shopping-preset-plans.enabled': {
    name: 'web.feature-flag.google-shopping-preset-plans.enabled',
    schema: booleanSchema,
  },
} as const satisfies Record<string, FeatureFlagDefinition>;

type FeatureFlagsSchema = typeof flagDefinitions;
type FeatureFlagDef<T extends FeatureFlags> = FeatureFlagsSchema[T];
export type FeatureFlags = keyof typeof flagDefinitions;
export type FeatureFlagInputType<T extends FeatureFlags> = FeatureFlagInput<
  FeatureFlagDef<T>['schema']
>;
export type FeatureFlagValueType<T extends FeatureFlags> = FeatureFlagOutput<
  FeatureFlagDef<T>['schema']
>;
export const featureFlagNames = Object.keys(flagDefinitions) as FeatureFlags[];
